import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cross, Heart, Flame, Sparkles, BookOpen, Crown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PRAYERS = [
  { title: "Oración de San Francisco por la paz", icon: Cross,
    text: `Señor, haz de mí un instrumento de tu paz:
donde haya odio, ponga yo amor;
donde haya ofensa, perdón;
donde haya discordia, unión;
donde haya error, verdad;
donde haya duda, fe;
donde haya desesperación, esperanza;
donde haya tinieblas, luz;
donde haya tristeza, alegría.

Oh, Maestro:
que no busque ser consolado, sino consolar;
ser comprendido, sino comprender;
ser amado, sino amar.
Porque dando se recibe;
olvidando se encuentra;
perdonando se es perdonado,
y muriendo se vive para la vida eterna. Amén.`,
  },
  { title: "Padre Nuestro", icon: Heart,
    text: `Padre nuestro, que estás en el cielo,
santificado sea tu Nombre;
venga a nosotros tu Reino;
hágase tu voluntad
en la tierra como en el cielo.

Danos hoy nuestro pan de cada día;
perdona nuestras ofensas,
como también nosotros perdonamos
a los que nos ofenden;
no nos dejes caer en la tentación,
y líbranos del mal. Amén.`,
  },
  { title: "Ven, Espíritu Santo", icon: Flame,
    text: `Ven, Espíritu Santo,
llena los corazones de tus fieles
y enciende en ellos el fuego de tu amor.
Envía, Señor, tu Espíritu;
y todo será creado,
y renovarás la faz de la tierra. Amén.`,
  },
  { title: "Salve, Reina y Madre", icon: Sparkles,
    text: `Dios te salve, Reina y Madre de misericordia,
vida, dulzura y esperanza nuestra;
Dios te salve.
A ti clamamos los desterrados hijos de Eva;
a ti suspiramos gimiendo y llorando
en este valle de lágrimas.
Ea, pues, Señora, abogada nuestra,
vuelve a nosotros tus ojos misericordiosos;
y después de este destierro muéstranos a Jesús,
fruto bendito de tu vientre.
¡Oh clemente, oh piadosa, oh dulce Virgen María! Amén.`,
  },
];

const CATEGORY_ICONS = {
  "Oraciones básicas": BookOpen,
  "Rosario": Sparkles,
  "Divina Misericordia": Heart,
  "Adoración Eucarística": Crown,
  "Oraciones Franciscanas": Flame,
};
const CATEGORY_ORDER = ["Oraciones básicas", "Rosario", "Divina Misericordia", "Adoración Eucarística", "Oraciones Franciscanas"];

export default function Oracion() {
  const [blocks, setBlocks] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    api.get("/content?section=oracion").then((r) => setBlocks(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setCms(r.data?.oracion || {})).catch(() => {});
  }, []);

  const groups = blocks.reduce((acc, b) => {
    const cat = b.category || "Otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(b);
    return acc;
  }, {});

  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => groups[c]),
    ...Object.keys(groups).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div data-testid="oracion-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-muted/40 to-background" style={cms.banner_url ? { backgroundImage: `url(${cms.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
        {cms.banner_url && <div className="absolute inset-0 bg-background/80" />}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-12 py-20 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-4">Espacio de oración</p>
          <h1 className="font-display text-5xl md:text-7xl text-primary leading-[1.05]" data-testid="oracion-title">
            {cms.title || (<>Rinconcito<br /><em>de oración</em></>)}
          </h1>
          <p className="mt-6 font-display text-2xl italic text-foreground/70 max-w-2xl mx-auto" data-testid="oracion-subtitle">
            {cms.subtitle || "«Orar es respirar el amor de Dios en familia»."}
          </p>
          {cms.paragraph && (
            <p className="mt-5 text-base leading-relaxed text-foreground/70 max-w-2xl mx-auto" data-testid="oracion-paragraph">{cms.paragraph}</p>
          )}
          {cms.image_url && (
            <img src={cms.image_url} alt="" className="mt-8 mx-auto max-h-48 rounded-2xl object-cover" />
          )}
        </div>
      </section>

      {/* Oraciones destacadas (estáticas) */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid md:grid-cols-2 gap-6">
          {PRAYERS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article key={i} className="rounded-2xl bg-card border border-border p-7 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`prayer-${i}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h2 className="font-display text-2xl text-primary">{p.title}</h2>
                </div>
                <pre className="font-display text-lg leading-relaxed whitespace-pre-wrap font-normal text-foreground/85" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {p.text}
                </pre>
              </article>
            );
          })}
        </div>
      </section>

      {/* Categorías desplegables */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-8 pb-20">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-2">Catálogo de oraciones</p>
          <h2 className="font-display text-3xl md:text-5xl text-primary">Por categorías</h2>
        </div>

        {orderedCategories.length === 0 && (
          <p className="text-center text-foreground/60">Cargando oraciones…</p>
        )}

        <div className="space-y-6 max-w-4xl mx-auto" data-testid="prayer-categories">
          {orderedCategories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || BookOpen;
            const items = groups[cat];
            return (
              <div key={cat} className="rounded-2xl bg-card border border-border overflow-hidden" data-testid={`cat-${cat.replace(/\s+/g, "-")}`}>
                <div className="px-6 py-4 bg-primary/5 border-b border-border flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="font-display text-2xl text-primary">{cat}</h3>
                  <span className="ml-auto text-xs tracking-wider uppercase text-foreground/60 font-semibold">{items.length}</span>
                </div>
                <Accordion type="single" collapsible className="w-full">
                  {items.map((b) => (
                    <AccordionItem key={b.id} value={b.id} className="px-6" data-testid={`pa-${b.id}`}>
                      <AccordionTrigger className="font-display text-lg text-foreground hover:text-primary">
                        {b.title}
                      </AccordionTrigger>
                      <AccordionContent>
                        {b.image_url && (
                          <img src={b.image_url} alt={b.title} className="w-full max-w-md rounded-xl mb-4 object-cover" />
                        )}
                        <pre className="font-display text-base leading-relaxed whitespace-pre-wrap font-normal text-foreground/85" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          {b.body}
                        </pre>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
